import { useState, useEffect, useRef } from 'react';
import Plotly from 'plotly.js-dist';
import { Tooltip } from 'react-tooltip';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import './HealthDataVisualizer.css';

console.log('📄 CancerPlanVisualizer.js module loaded');

// Framework configuration
const splits = [4, 4, 4, 3, 8, 8, 6, 6, 11, 11, 7, 4];

const section_names = [
  'Outcomes',
  'Objectives', 
  'Outputs',
  'Functions',
  'Threats',
  'Opportunities',
  'Strategy',
  'Governance and Organization',
  'Financing',
  'Resource Management',
  'Health Services',
  'Implementation'
];

// Framework definitions for tooltips
const FRAMEWORK_DEFINITIONS = {
  'Outcomes': 'How well is the health system performing in relation to cancer care and control concerning health system outcomes?',
  'Objectives': 'How well is the health system producing health services for cancer in terms of the four value dimensions?',
  'Outputs': 'Is the health system delivering the right health services to respond to cancer?',
  'Functions': 'How are health system inputs such as governance and organisation, financing, and resource management currently generated and used in health systems to deliver public and individual health services for cancer control?',
  'Threats': 'What contextual threats will influence cancer demands on the health system and influence the system response to cancer care and control?',
  'Opportunities': 'What contextual opportunities will influence cancer demands on the health system and influence the system response to cancer care and control?',
  'Strategy': 'How will the challenge identified be addressed to improve health system performance and create value in relation to cancer?',
  'Governance and Organization': 'What reforms are to be undertaken in terms of governance and organization of the health system to achieve the articulated strategy to improve cancer care and control?',
  'Financing': 'What reforms are to be undertaken in terms of financing of the health system to achieve the articulated strategy to improve cancer care and control?',
  'Resource Management': 'What reforms are to be undertaken in terms of resource management of the health system to achieve the articulated strategy to improve cancer care and control?',
  'Health Services': 'What reforms are to be undertaken in terms of the delivery of health services to achieve the articulated strategy to improve cancer care and control?',
  'Implementation': 'How will the plan be implemented, measured, stakeholder alignment and sustained changed management achieved?'
};

// Max scores dictionary
const max_scores_dict = {
  "Health": 5, "Financial Risk Protection": 5, "User Satisfaction": 5, "Psychosocial Support Services": 5,
  "Effectiveness": 5, "Efficiency": 4, "Equity": 4, "Responsiveness": 4,
  "Individual Health Services": 5, "Population Health Services": 4, "Community-Based Palliative Care": 5, "Cancer Surveillance Systems": 5,
  "Governance and Organisation": 5, "Financing": 5, "Resource Management": 5,
  "Demographic Threats": 4, "Epidemiologic Threats": 4, "Political Threats": 4, "Legal Threats": 4,
  "Sociocultural Threats": 4, "Economic Threats": 4, "Ecological Threats": 4, "Technological Threats": 4,
  "Demographic Opportunities": 4, "Epidemiologic Opportunities": 4, "Political Opportunities": 4, "Legal Opportunities": 4,
  "Sociocultural Opportunities": 4, "Economic Opportunities": 4, "Ecological Opportunities": 4, "Technological Opportunities": 4,
  "Vision": 3, "Mission": 3, "Goals": 3, "Objectives": 3, "Values": 2, "Integration with Noncommunicable Disease Programs": 5,
  "Macro-organization": 2, "Governance": 5, "Policy": 5, "Regulation": 5, "Decentralization": 2, "Strategic public private partnerships": 5,
  "Cost measurement systems": 3, "Current financing and fiscal space": 5, "Proposed funding to implement plan": 3, "Sources of funds": 5,
  "Resource Mobilization Strategies": 5, "Financing for Governance and Oversight": 5, "Pooling of funds": 2, "Channeling of funds": 2,
  "Allocation of funds": 4, "Payment mechanisms for providers": 4, "Payment mechanisms for capital investments": 3,
  "Human resources": 5, "Capacity Building for Health Professionals": 5, "Infrastructure": 5, "Pharmaceuticals and medical supplies": 5,
  "Patient Navigation": 5, "Information technology and data systems": 5, "Supply chain management": 3, "Knowledge Generation": 3,
  "Innovation ecosystem": 3, "Education Initiatives": 5, "Innovation Initiatives": 5,
  "Public Health Services (Health promotion)": 5, "Public Health Services (Health protection)": 5, "Public Health Services (Disease Prevention)": 5,
  "Personal Healthcare Services (Diagnosis)": 4, "Personal Healthcare Services (Treatment)": 4, "Personal Healthcare Services (Palliation and rehabilitative care)": 4,
  "Provider Value Enhancement": 3,
  "Monitoring and Evaluation framework": 2, "Change management": 2, "Risk and Mitigation Strategies": 3, "Stakeholder engagement": 3
};

// Build reverse mapping: sub-element -> section
const SUBELEMENT_TO_SECTION = {};
const subElementKeys = Object.keys(max_scores_dict);
let cumulativeIndex = 0;

splits.forEach((splitCount, sectionIndex) => {
  const sectionName = section_names[sectionIndex];
  for (let i = 0; i < splitCount; i++) {
    const subElement = subElementKeys[cumulativeIndex + i];
    if (subElement) {
      SUBELEMENT_TO_SECTION[subElement] = sectionName;
    }
  }
  cumulativeIndex += splitCount;
});

// Build section to sub-elements mapping
const SECTION_TO_SUBELEMENTS = {};
section_names.forEach(section => {
  SECTION_TO_SUBELEMENTS[section] = [];
});
Object.entries(SUBELEMENT_TO_SECTION).forEach(([subElement, section]) => {
  if (SECTION_TO_SUBELEMENTS[section]) {
    SECTION_TO_SUBELEMENTS[section].push(subElement);
  }
});

// Color palette for sections
const sectionColors = [
  '#06B6D4', '#FBBF24', '#A78BFA', '#3B82F6', '#EC4899', '#9CA3AF',
  '#FB923C', '#60A5FA', '#38BDF8', '#22D3EE', '#F97316', '#2DD4BF'
];

// Searchable Single-Select Component
const SearchableSingleSelect = ({ options, selected, onSelect, placeholder, id, disabled = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  const filteredOptions = options.filter(opt => 
    opt.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const displayText = selected || placeholder;

  return (
    <div className="dropdown-container" id={id} ref={dropdownRef}>
      <div 
        className={`dropdown-header ${disabled ? 'disabled' : ''}`} 
        onClick={() => !disabled && setIsOpen(!isOpen)}
        style={disabled ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
      >
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={displayText}
          onClick={(e) => {
            e.stopPropagation();
            if (!disabled) setIsOpen(true);
          }}
          className="dropdown-search-input"
          disabled={disabled}
        />
        <span className="dropdown-arrow">{isOpen ? '▲' : '▼'}</span>
      </div>
      {isOpen && !disabled && (
        <div className="dropdown-menu">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((opt, index) => (
              <div
                key={index}
                className={`dropdown-item ${selected === opt ? 'selected' : ''}`}
                onClick={() => {
                  onSelect(opt);
                  setIsOpen(false);
                  setSearchTerm('');
                }}
              >
                <span>{opt}</span>
              </div>
            ))
          ) : (
            <div className="dropdown-item no-options">No options found</div>
          )}
        </div>
      )}
    </div>
  );
};

// Main Component
const CancerPlanVisualizer = () => {
  console.log('🚀 CancerPlanVisualizer component rendering...');
  
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [data, setData] = useState([]);
  const [countries, setCountries] = useState([]);
  
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedElement, setSelectedElement] = useState(null);
  const [selectedSubElement, setSelectedSubElement] = useState(null);
  
  const [countryData, setCountryData] = useState([]);
  const [averagedScores, setAveragedScores] = useState([]);
  const [sectionDetailedData, setSectionDetailedData] = useState([]);
  
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [showData, setShowData] = useState(false);

  const radialChartRef = useRef(null);
  const distributionChartRef = useRef(null);
  const contentAreaRef = useRef(null);

  // Get available sub-elements based on selected element
  const availableSubElements = selectedElement 
    ? SECTION_TO_SUBELEMENTS[selectedElement] || []
    : [];

  // Load CSV data
  useEffect(() => {
    console.log('📦 useEffect triggered - about to load data...');
    
    const loadData = async () => {
      console.log('🔄 Starting to load CSV data...');
      try {
        setLoading(true);
        
        // Try multiple possible paths
        const possiblePaths = [
          '/data/s3_cancer_plan_output.csv',
          '/s3_cancer_plan_output.csv',
          './data/s3_cancer_plan_output.csv',
          './s3_cancer_plan_output.csv'
        ];
        
        let response = null;
        let successPath = null;
        
        for (const path of possiblePaths) {
          try {
            console.log(`📂 Trying path: ${path}`);
            const testResponse = await fetch(path);
            if (testResponse.ok) {
              response = testResponse;
              successPath = path;
              console.log(`✅ Success with path: ${path}`);
              break;
            } else {
              console.log(`❌ Failed with path: ${path} - Status: ${testResponse.status}`);
            }
          } catch (e) {
            console.log(`❌ Error with path: ${path} - ${e.message}`);
          }
        }
        
        if (!response) {
          throw new Error('Could not load CSV from any path. Make sure s3_cancer_plan_output.csv is in your public/data/ folder.');
        }
        
        const text = await response.text();
        console.log(`✅ CSV loaded from ${successPath}, length: ${text.length} characters`);
        console.log('📄 First 200 chars:', text.substring(0, 200));
        
        const lines = text.split('\n');
        console.log(`📊 Total lines in CSV: ${lines.length}`);
        
        const headers = lines[0].split(',');
        console.log('📋 Headers:', headers);
        
        const parsedData = [];
        
        for (let i = 1; i < lines.length; i++) {
          if (!lines[i].trim()) continue;
          
          const values = [];
          let currentValue = '';
          let insideQuotes = false;
          
          for (let char of lines[i]) {
            if (char === '"') {
              insideQuotes = !insideQuotes;
            } else if (char === ',' && !insideQuotes) {
              values.push(currentValue.trim());
              currentValue = '';
            } else {
              currentValue += char;
            }
          }
          values.push(currentValue.trim());
          
          if (values.length === headers.length) {
            const row = {};
            headers.forEach((header, index) => {
              row[header.trim()] = values[index];
            });
            
            const subElement = row['Sub-Element'];
            const score = parseFloat(row.score);
            const maxScore = max_scores_dict[subElement];
            
            if (maxScore && score > maxScore) {
              row.score = 'error';
            }
            
            parsedData.push(row);
          }
        }
        
        console.log(`✅ Parsed ${parsedData.length} data rows`);
        if (parsedData.length > 0) {
          console.log('🌍 First row sample:', parsedData[0]);
        }
        
        setData(parsedData);
        
        const uniqueCountries = [...new Set(parsedData.map(row => row.country))].sort();
        console.log(`🌎 Unique countries found: ${uniqueCountries.length}`, uniqueCountries);
        setCountries(uniqueCountries);
        
        // Default to Australia
        if (uniqueCountries.includes('Australia')) {
          setSelectedCountry('Australia');
          console.log('🦘 Default country set to Australia');
        } else if (uniqueCountries.length > 0) {
          setSelectedCountry(uniqueCountries[0]);
          console.log(`🌍 Default country set to ${uniqueCountries[0]}`);
        } else {
          console.warn('⚠️ No countries found in data!');
        }
        
        setLoading(false);
      } catch (error) {
        console.error('❌ Error loading data:', error);
        console.error('❌ Error details:', error.message, error.stack);
        setLoadError(error.message);
        setLoading(false);
      }
    };

    loadData();
    console.log('📞 loadData() called');
  }, []);

  // Filter data when country changes
  useEffect(() => {
    if (!selectedCountry || data.length === 0) {
      setCountryData([]);
      return;
    }
    
    const filtered = data.filter(row => row.country === selectedCountry);
    setCountryData(filtered);
  }, [selectedCountry, data]);

  // Calculate averaged scores when country data changes
  useEffect(() => {
    if (countryData.length === 0) {
      setAveragedScores([]);
      return;
    }
    
    const scores = [];
    const subElements = [];
    
    countryData.forEach(row => {
      const subElement = row['Sub-Element'];
      const score = row.score === 'error' ? NaN : parseFloat(row.score);
      
      if (SUBELEMENT_TO_SECTION[subElement] && !isNaN(score)) {
        scores.push(score);
        subElements.push(subElement);
      }
    });
    
    const sections = [];
    let index = 0;
    
    splits.forEach((count, sectionIndex) => {
      const sectionScores = [];
      const sectionSubElements = subElements.slice(index, index + count);
      const rawScores = scores.slice(index, index + count);
      
      rawScores.forEach((score, i) => {
        const subElement = sectionSubElements[i];
        const maxScore = max_scores_dict[subElement] || 5;
        const normalized = (score / maxScore) * 5;
        sectionScores.push(normalized);
      });
      
      const avg = sectionScores.length > 0 
        ? sectionScores.reduce((a, b) => a + b, 0) / sectionScores.length 
        : 0;
      
      sections.push(avg);
      index += count;
    });
    
    setAveragedScores(sections);
  }, [countryData]);

  // Get section detailed data when element changes
  useEffect(() => {
    if (!selectedElement || countryData.length === 0) {
      setSectionDetailedData([]);
      return;
    }
    
    const sectionData = countryData.filter(row => {
      const subElement = row['Sub-Element'];
      return SUBELEMENT_TO_SECTION[subElement] === selectedElement;
    });
    
    const processedData = sectionData
      .filter(row => row.score !== 'error' && !isNaN(parseFloat(row.score)))
      .map(row => {
        const score = parseFloat(row.score);
        const maxScore = max_scores_dict[row['Sub-Element']] || 5;
        return {
          subElement: row['Sub-Element'],
          score: score,
          maxScore: maxScore,
          normalizedScore: (score / maxScore) * 5,
          response: row.response || 'No response available',
          reasoning: row.reasoning || 'No reasoning available'
        };
      });
    
    setSectionDetailedData(processedData);
  }, [selectedElement, countryData]);

  // Render radial chart
  useEffect(() => {
    if (!radialChartRef.current || averagedScores.length === 0) {
      if (radialChartRef.current) {
        Plotly.purge(radialChartRef.current);
      }
      return;
    }

    const chartData = [{
      type: 'barpolar',
      r: averagedScores,
      theta: section_names,
      marker: {
        color: sectionColors,
        line: { color: 'white', width: 2 }
      },
      hovertemplate: '<b>%{theta}</b><br>Score: %{r:.2f} / 5.0<br><i>Click to view details</i><extra></extra>'
    }];

    const layout = {
      polar: {
        radialaxis: {
          visible: true,
          range: [0, 5],
          tickvals: [0, 1, 2, 3, 4, 5],
          ticktext: ['0', '1', '2', '3', '4', '5'],
          tickfont: { size: 12, color: '#6B7280' },
          gridcolor: '#E5E7EB',
          showline: false
        },
        angularaxis: {
          tickfont: { size: 10, weight: 'bold', color: '#1F2937' },
          direction: 'clockwise',
          rotation: 90,
          gridcolor: '#E5E7EB'
        },
        bgcolor: 'white',
        bargap: 0.1
      },
      showlegend: false,
      paper_bgcolor: 'white',
      plot_bgcolor: 'white',
      margin: { t: 40, b: 40, l: 80, r: 80 },
      autosize: true
    };

    const config = {
      responsive: true,
      displayModeBar: false,
      displaylogo: false
    };

    Plotly.newPlot(radialChartRef.current, chartData, layout, config);

    radialChartRef.current.on('plotly_click', function(eventData) {
      if (eventData.points && eventData.points.length > 0) {
        const section = eventData.points[0].theta;
        setSelectedElement(section);
        setSelectedSubElement(null);
      }
    });

    return () => {
      if (radialChartRef.current) {
        radialChartRef.current.removeAllListeners('plotly_click');
      }
    };
  }, [averagedScores]);

  // Render distribution chart when element is selected
  useEffect(() => {
    if (!distributionChartRef.current || sectionDetailedData.length === 0 || !selectedElement) {
      if (distributionChartRef.current) {
        Plotly.purge(distributionChartRef.current);
      }
      return;
    }

    const normalizedScores = sectionDetailedData.map(item => item.normalizedScore);
    const subElements = sectionDetailedData.map(item => item.subElement);
    
    const abbreviatedLabels = subElements.map(label => {
      if (label.length > 25) {
        return label.substring(0, 22) + '...';
      }
      return label;
    });

    const chartData = [{
      type: 'bar',
      x: abbreviatedLabels,
      y: normalizedScores,
      text: subElements,
      marker: {
        color: normalizedScores.map(score => {
          if (score < 1.5) return '#EF4444';
          if (score < 2.5) return '#F97316';
          if (score < 3.5) return '#EAB308';
          if (score < 4.5) return '#84CC16';
          return '#22C55E';
        }),
        line: { color: '#374151', width: 1 }
      },
      hovertemplate: '<b>%{text}</b><br>Normalized Score: %{y:.2f} / 5.0<br><i>Click for details</i><extra></extra>'
    }];

    const layout = {
      title: {
        text: `${selectedElement} - Sub-Element Scores`,
        font: { size: 16, color: '#1F2937' }
      },
      xaxis: {
        title: '',
        tickangle: -45,
        tickfont: { size: 10 },
        automargin: true
      },
      yaxis: {
        title: 'Normalized Score',
        range: [0, 5.5],
        tickvals: [0, 1, 2, 3, 4, 5],
        tickfont: { size: 12 }
      },
      paper_bgcolor: 'white',
      plot_bgcolor: '#f9fafb',
      margin: { t: 60, b: 120, l: 60, r: 40 },
      height: 350
    };

    const config = {
      responsive: true,
      displayModeBar: false,
      displaylogo: false
    };

    Plotly.newPlot(distributionChartRef.current, chartData, layout, config);

    distributionChartRef.current.on('plotly_click', function(eventData) {
      if (eventData.points && eventData.points.length > 0) {
        const subElement = eventData.points[0].text;
        setSelectedSubElement(subElement);
      }
    });

    return () => {
      if (distributionChartRef.current) {
        distributionChartRef.current.removeAllListeners('plotly_click');
      }
    };
  }, [sectionDetailedData, selectedElement]);

  // Get sub-element details
  const getSubElementDetails = () => {
    if (!selectedSubElement || sectionDetailedData.length === 0) return null;
    return sectionDetailedData.find(item => item.subElement === selectedSubElement);
  };

  const subElementDetails = getSubElementDetails();

  // Export to PNG
  const exportToPNG = async () => {
    const contentArea = contentAreaRef.current;
    if (!contentArea) return;
    
    try {
      const canvas = await html2canvas(contentArea, { 
        scale: 2,
        backgroundColor: '#ffffff'
      });
      
      const finalCanvas = document.createElement('canvas');
      const ctx = finalCanvas.getContext('2d');
      finalCanvas.width = canvas.width;
      finalCanvas.height = canvas.height;
      
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, finalCanvas.width, finalCanvas.height);
      ctx.drawImage(canvas, 0, 0);
      
      const currentYear = new Date().getFullYear();
      ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
      ctx.font = 'italic 22px Arial, sans-serif';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'bottom';
      ctx.fillText(`© Health Systems Innovation Lab, Harvard University, ${currentYear}`, 40, canvas.height - 30);
      
      finalCanvas.toBlob(blob => {
        const fileName = `cancer_plan_${(selectedCountry || 'analysis').replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.png`;
        saveAs(blob, fileName);
      });
    } catch (error) {
      console.error('Error exporting to PNG:', error);
    }
  };

  // Calculate overall score
  const calculateOverallScore = () => {
    if (averagedScores.length === 0) return 0;
    const sum = averagedScores.reduce((a, b) => a + b, 0);
    return (sum / averagedScores.length).toFixed(2);
  };

  const toggleData = () => setShowData(!showData);

  console.log('🔍 Current state:', { loading, loadError, countriesCount: countries.length, selectedCountry });

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-message">Loading Cancer Control Plan Data...</div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="loading-container">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ color: '#DC2626', fontSize: '1.2rem', marginBottom: '16px' }}>
            ❌ Error Loading Data
          </div>
          <div style={{ color: '#6B7280', marginBottom: '16px' }}>
            {loadError}
          </div>
          <div style={{ color: '#9CA3AF', fontSize: '0.9rem' }}>
            Make sure the CSV file is placed in: <code>public/data/s3_cancer_plan_output.csv</code>
          </div>
        </div>
      </div>
    );
  }

  if (countries.length === 0) {
    return (
      <div className="loading-container">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{ color: '#F59E0B', fontSize: '1.2rem', marginBottom: '16px' }}>
            ⚠️ No Data Found
          </div>
          <div style={{ color: '#6B7280' }}>
            The CSV file was loaded but no country data was found. Check the console for details.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="explorer-container" 
      data-sidebar-open={isSidebarOpen.toString()}
      style={{ '--sidebar-width': isSidebarOpen ? '320px' : '40px' }}
    >
      {/* Sidebar */}
      <div className={`sidebar ${isSidebarOpen ? 'open' : 'collapsed'}`}>
        {isSidebarOpen && (
          <div className="sidebar-content">
            {/* Country Dropdown */}
            <div className="section">
              <h2 className="section-title">Country</h2>
              <SearchableSingleSelect
                options={countries}
                selected={selectedCountry}
                onSelect={(country) => {
                  setSelectedCountry(country);
                  setSelectedElement(null);
                  setSelectedSubElement(null);
                }}
                placeholder="Select Country"
                id="country-select"
              />
            </div>

            {/* Element Dropdown */}
            <div className="section">
              <h2 className="section-title">Element (Section)</h2>
              <SearchableSingleSelect
                options={section_names}
                selected={selectedElement}
                onSelect={(element) => {
                  setSelectedElement(element);
                  setSelectedSubElement(null);
                }}
                placeholder="Select Element"
                id="element-select"
                disabled={!selectedCountry}
              />
              {selectedElement && FRAMEWORK_DEFINITIONS[selectedElement] && (
                <div style={{
                  marginTop: '8px',
                  padding: '10px',
                  backgroundColor: '#F3F4F6',
                  borderRadius: '6px',
                  borderLeft: '3px solid #3B82F6',
                  fontSize: '0.8rem',
                  color: '#4B5563',
                  lineHeight: '1.5'
                }}>
                  {FRAMEWORK_DEFINITIONS[selectedElement]}
                </div>
              )}
            </div>

            {/* Sub-Element Dropdown */}
            <div className="section">
              <h2 className="section-title">Sub-Element</h2>
              <SearchableSingleSelect
                options={availableSubElements}
                selected={selectedSubElement}
                onSelect={setSelectedSubElement}
                placeholder="Select Sub-Element"
                id="subelement-select"
                disabled={!selectedElement}
              />
            </div>

            {/* Overall Score Display */}
            {selectedCountry && averagedScores.length > 0 && (
              <div className="section" style={{
                backgroundColor: '#F0FDF4',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #BBF7D0'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.9rem', fontWeight: '500', color: '#166534' }}>
                    Overall Score
                  </span>
                  <span style={{ fontSize: '1.25rem', fontWeight: '700', color: '#15803D' }}>
                    {calculateOverallScore()} / 5.0
                  </span>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="section">
              <button 
                className="button button-secondary"
                onClick={toggleData}
                style={{ width: '100%', marginBottom: '8px' }}
              >
                {showData ? 'Hide Data Table' : 'Show Data Table'}
              </button>
              <button 
                onClick={exportToPNG}
                style={{ 
                  width: '100%', 
                  padding: '10px 16px',
                  backgroundColor: '#3B82F6', 
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
                disabled={!selectedCountry}
              >
                Export as PNG
              </button>
            </div>

            {/* Elements Legend with Tooltips */}
            <div className="section" style={{ 
              marginTop: '20px', 
              paddingTop: '16px', 
              borderTop: '1px solid #E5E7EB' 
            }}>
              <h2 className="section-title">Elements Legend</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                {section_names.map((section, index) => (
                  <div 
                    key={section}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '6px 8px',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      backgroundColor: selectedElement === section ? '#DBEAFE' : 'transparent',
                      transition: 'background-color 0.15s'
                    }}
                    onClick={() => {
                      setSelectedElement(section);
                      setSelectedSubElement(null);
                    }}
                    data-tooltip-id={`legend-tooltip-${index}`}
                    data-tooltip-content={FRAMEWORK_DEFINITIONS[section]}
                    onMouseEnter={(e) => {
                      if (selectedElement !== section) {
                        e.currentTarget.style.backgroundColor = '#F3F4F6';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedElement !== section) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <span 
                      style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '2px',
                        backgroundColor: sectionColors[index],
                        flexShrink: 0
                      }}
                    />
                    <span style={{ 
                      fontSize: '0.75rem', 
                      color: '#4B5563',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {section}
                    </span>
                    <Tooltip 
                      id={`legend-tooltip-${index}`} 
                      place="right" 
                      style={{ maxWidth: '250px', zIndex: 10002 }} 
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="main-content">
        <div className="header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 className="header-title">
            {selectedCountry ? `${selectedCountry} - National Cancer Control Plan Analysis` : 'Select a Country'}
          </h1>
        </div>
        
        <div className="content-area" ref={contentAreaRef} style={{ padding: '24px', overflowY: 'auto' }}>
          {!selectedCountry ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#6B7280',
              fontSize: '1.1rem'
            }}>
              <p>Please select a country to view the cancer control plan analysis.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* Radial Chart */}
              <div style={{
                backgroundColor: '#ffffff',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
              }}>
                <h3 style={{ 
                  fontSize: '1.1rem', 
                  fontWeight: '600', 
                  color: '#1F2937', 
                  margin: '0 0 16px 0',
                  textAlign: 'center'
                }}>
                  Framework Assessment Overview
                </h3>
                <div 
                  ref={radialChartRef} 
                  style={{ width: '100%', height: '500px' }}
                />
              </div>

              {/* Distribution Chart */}
              {selectedElement && (
                <div style={{
                  backgroundColor: '#ffffff',
                  borderRadius: '12px',
                  padding: '20px',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}>
                  <div 
                    ref={distributionChartRef} 
                    style={{ width: '100%', height: '350px' }}
                  />
                </div>
              )}

              {/* Sub-Element Details Card */}
              {subElementDetails && (
                <div style={{
                  backgroundColor: '#ffffff',
                  borderRadius: '12px',
                  padding: '24px',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                  borderLeft: '4px solid #3B82F6'
                }}>
                  <h3 style={{ 
                    fontSize: '1.25rem', 
                    fontWeight: '600', 
                    color: '#1F2937', 
                    margin: '0 0 16px 0' 
                  }}>
                    {subElementDetails.subElement}
                  </h3>
                  
                  <div style={{ display: 'flex', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      padding: '12px 16px',
                      backgroundColor: '#F3F4F6',
                      borderRadius: '8px',
                      minWidth: '100px'
                    }}>
                      <span style={{ fontSize: '0.75rem', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Raw Score
                      </span>
                      <span style={{ fontSize: '1.25rem', fontWeight: '700', color: '#1F2937' }}>
                        {subElementDetails.score} / {subElementDetails.maxScore}
                      </span>
                    </div>
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      padding: '12px 16px',
                      backgroundColor: '#DBEAFE',
                      borderRadius: '8px',
                      minWidth: '100px'
                    }}>
                      <span style={{ fontSize: '0.75rem', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Normalized
                      </span>
                      <span style={{ fontSize: '1.25rem', fontWeight: '700', color: '#1F2937' }}>
                        {subElementDetails.normalizedScore.toFixed(2)} / 5.0
                      </span>
                    </div>
                  </div>
                  
                  <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #E5E7EB' }}>
                    <h4 style={{ fontSize: '0.9rem', fontWeight: '600', color: '#374151', margin: '0 0 8px 0' }}>
                      Response
                    </h4>
                    <p style={{ fontSize: '0.9rem', color: '#4B5563', lineHeight: '1.6', margin: 0 }}>
                      {subElementDetails.response}
                    </p>
                  </div>
                  
                  <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #E5E7EB' }}>
                    <h4 style={{ fontSize: '0.9rem', fontWeight: '600', color: '#374151', margin: '0 0 8px 0' }}>
                      Reasoning
                    </h4>
                    <p style={{ fontSize: '0.9rem', color: '#4B5563', lineHeight: '1.6', margin: 0 }}>
                      {subElementDetails.reasoning}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Data Table Modal */}
      {showData && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            zIndex: 10001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '2rem'
          }}
          onClick={toggleData}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              maxWidth: '90vw',
              maxHeight: '80vh',
              width: '100%',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              padding: '16px 24px',
              borderBottom: '1px solid #E5E7EB'
            }}>
              <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600', color: '#1F2937' }}>
                Data Table - {selectedCountry}
              </h3>
              <button
                onClick={toggleData}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#6B7280',
                  padding: '4px'
                }}
              >
                ×
              </button>
            </div>
            
            <div style={{ flex: 1, overflow: 'auto', padding: 0 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead style={{ position: 'sticky', top: 0, backgroundColor: '#F3F4F6', zIndex: 1 }}>
                  <tr>
                    <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #E5E7EB', fontWeight: '600', color: '#374151' }}>Element</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #E5E7EB', fontWeight: '600', color: '#374151' }}>Sub-Element</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #E5E7EB', fontWeight: '600', color: '#374151' }}>Score</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #E5E7EB', fontWeight: '600', color: '#374151' }}>Max Score</th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', borderBottom: '1px solid #E5E7EB', fontWeight: '600', color: '#374151' }}>Normalized</th>
                  </tr>
                </thead>
                <tbody>
                  {countryData.map((row, index) => {
                    const subElement = row['Sub-Element'];
                    const section = SUBELEMENT_TO_SECTION[subElement];
                    const score = row.score === 'error' ? 'Error' : parseFloat(row.score);
                    const maxScore = max_scores_dict[subElement] || 5;
                    const normalized = row.score === 'error' ? 'N/A' : ((score / maxScore) * 5).toFixed(2);
                    
                    return (
                      <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F9FAFB' }}>
                        <td style={{ padding: '12px 16px', borderBottom: '1px solid #E5E7EB' }}>{section}</td>
                        <td style={{ padding: '12px 16px', borderBottom: '1px solid #E5E7EB' }}>{subElement}</td>
                        <td style={{ 
                          padding: '12px 16px', 
                          borderBottom: '1px solid #E5E7EB',
                          color: row.score === 'error' ? '#DC2626' : 'inherit',
                          fontWeight: row.score === 'error' ? '500' : 'normal'
                        }}>
                          {row.score === 'error' ? 'Error' : score}
                        </td>
                        <td style={{ padding: '12px 16px', borderBottom: '1px solid #E5E7EB' }}>{maxScore}</td>
                        <td style={{ padding: '12px 16px', borderBottom: '1px solid #E5E7EB' }}>{normalized}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Tooltips */}
      <Tooltip id="element-info-tooltip" place="right" />
    </div>
  );
};

export default CancerPlanVisualizer;